import { useEffect, useRef } from "react";

/**
 * Ambient animated background — canvas-based, GPU-accelerated where possible.
 *
 * Visual language:
 *  - Deep space void (near-black) with a slowly breathing amber vignette
 *  - Constellation grid of drifting particles connected by delicate lines
 *  - Two radio-signal oscilloscope curves undulating across the width
 *  - A soft spotlight that follows the cursor (parallax feel)
 *
 * Everything renders on a single <canvas> for performance, sized to devicePixelRatio,
 * with a device-appropriate particle count and reduced motion respected.
 */
export default function AmbientBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let raf;
    let running = true;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Track pointer for spotlight
    const pointer = { x: 0.5, y: 0.4, tx: 0.5, ty: 0.4 };
    const onMove = (e) => {
      const touch = e.touches ? e.touches[0] : e;
      pointer.tx = touch.clientX / window.innerWidth;
      pointer.ty = touch.clientY / window.innerHeight;
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });

    // Sizing
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;
    let particles = [];
    const isSmall = () => window.innerWidth < 900;

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Rebuild particle field to match viewport size
      const count = isSmall() ? 40 : 80;
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.15,
        vy: (Math.random() - 0.5) * 0.15,
        r: Math.random() * 1.3 + 0.4,
        life: Math.random() * Math.PI * 2,
      }));
    };
    resize();
    window.addEventListener("resize", resize);

    const CONNECT_DIST = 130;
    let t0 = performance.now();

    const draw = (now) => {
      if (!running) return;
      const dt = (now - t0) / 1000;
      t0 = now;

      // Smoothed pointer (parallax feel)
      pointer.x += (pointer.tx - pointer.x) * 0.06;
      pointer.y += (pointer.ty - pointer.y) * 0.06;

      // Base wash — deep near-black with subtle diagonal falloff
      const grad = ctx.createLinearGradient(0, 0, w, h);
      grad.addColorStop(0, "#050505");
      grad.addColorStop(0.6, "#000000");
      grad.addColorStop(1, "#08070B");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      // Cursor spotlight (radial glow)
      const spotX = pointer.x * w;
      const spotY = pointer.y * h;
      const spot = ctx.createRadialGradient(spotX, spotY, 0, spotX, spotY, Math.max(w, h) * 0.6);
      spot.addColorStop(0, "rgba(250, 204, 21, 0.055)");
      spot.addColorStop(0.35, "rgba(250, 204, 21, 0.015)");
      spot.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = spot;
      ctx.fillRect(0, 0, w, h);

      // Oscilloscope curves — two overlapping radio-signal lines
      const now01 = now / 1000;
      for (let l = 0; l < 2; l++) {
        ctx.beginPath();
        const baseY = h * (l === 0 ? 0.35 : 0.72);
        const amp = h * (l === 0 ? 0.06 : 0.045);
        const freq = l === 0 ? 0.008 : 0.011;
        const speed = l === 0 ? 0.9 : 0.6;
        for (let x = 0; x <= w; x += 6) {
          const y =
            baseY +
            Math.sin(x * freq + now01 * speed) * amp +
            Math.sin(x * freq * 2.3 + now01 * speed * 1.6) * (amp * 0.35);
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = l === 0 ? "rgba(250, 204, 21, 0.18)" : "rgba(255, 255, 255, 0.08)";
        ctx.lineWidth = l === 0 ? 1.2 : 0.9;
        ctx.stroke();
      }

      // Update + draw particle constellation
      const speed = reduce ? 0.15 : 1;
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx * speed;
        p.y += p.vy * speed;
        p.life += 0.02 * speed;
        // Wrap
        if (p.x < -20) p.x = w + 20;
        else if (p.x > w + 20) p.x = -20;
        if (p.y < -20) p.y = h + 20;
        else if (p.y > h + 20) p.y = -20;

        const alpha = 0.35 + Math.sin(p.life) * 0.35;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${Math.max(0.05, alpha * 0.55)})`;
        ctx.fill();
      }

      // Connections between close particles
      ctx.lineWidth = 0.5;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i];
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.hypot(dx, dy);
          if (dist < CONNECT_DIST) {
            const alpha = (1 - dist / CONNECT_DIST) * 0.16;
            ctx.strokeStyle = `rgba(250, 204, 21, ${alpha.toFixed(3)})`;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Vertical scanline pass (subtle CRT feel)
      ctx.globalCompositeOperation = "overlay";
      const scanH = 3;
      for (let y = 0; y < h; y += scanH) {
        ctx.fillStyle = y % (scanH * 2) === 0 ? "rgba(255,255,255,0.008)" : "rgba(0,0,0,0.04)";
        ctx.fillRect(0, y, w, scanH);
      }
      ctx.globalCompositeOperation = "source-over";

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="ambient-background"
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
      aria-hidden="true"
    />
  );
}
